import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import { movies } from "@/mocks/movies";
import { collectionLabels } from "@/mocks/categories";
import { categoryTiles } from "@/mocks/categories";
import FilterBar from "./components/FilterBar";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 8;

const collectionIntro: Record<string, { title: string; desc: string }> = {
  new: {
    title: "最新韩国三级电影推荐",
    desc: "按上线时间整理的新片资料，包含剧情概要、主创阵容与编辑解析，适合想第一时间了解新作的观众。",
  },
  classic: {
    title: "经典韩国三级电影收藏",
    desc: "十年以上仍被反复讨论的作品合集。这些影片在叙事手法与影像风格上影响了不少后来的创作者。",
  },
  hot: {
    title: "热门韩国三级电影榜单",
    desc: "按站内热度与讨论量排序，每周更新一次。适合想先看看大家都在聊什么的观众。",
  },
  "by-year": {
    title: "按年份浏览韩国影视作品",
    desc: "从 2010 到 2025，逐年梳理题材与风格的变化，可按年份筛选查看。",
  },
  all: {
    title: "全部韩国三级电影资料",
    desc: "站内收录的全部影片资料。可通过类型、年份与排序快速缩小范围，找到想看的那一部。",
  },
};

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [visible, setVisible] = useState(PAGE_SIZE);

  const collection = searchParams.get("c") ?? "all";
  const genre = searchParams.get("genre") ?? "全部";
  const year = searchParams.get("year") ?? "all";
  const sort = searchParams.get("sort") ?? "latest";
  const requestedPage = Number(searchParams.get("page") ?? "1") || 1;

  const updateParam = (key: string, value: string, defaultValue: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    next.delete("page");
    setSearchParams(next);
    setVisible(PAGE_SIZE);
  };

  const resetFilters = () => {
    const next = new URLSearchParams();
    if (collection && collection !== "all") next.set("c", collection);
    setSearchParams(next);
    setVisible(PAGE_SIZE);
  };

  const filtered = useMemo(() => {
    let list = [...movies];

    if (collection === "new" || collection === "classic" || collection === "hot") {
      list = list.filter((movie) => movie.collection.includes(collection));
    }

    if (genre !== "全部") {
      list = list.filter((movie) => movie.genres.includes(genre));
    }

    if (year === "2024") list = list.filter((movie) => movie.year >= 2024);
    if (year === "2020") list = list.filter((movie) => movie.year >= 2020 && movie.year <= 2023);
    if (year === "2015") list = list.filter((movie) => movie.year >= 2015 && movie.year <= 2019);
    if (year === "classic") list = list.filter((movie) => movie.year <= 2014);

    if (sort === "hot") list.sort((a, b) => b.views - a.views);
    else if (sort === "rating") list.sort((a, b) => b.rating - a.rating);
    else list.sort((a, b) => b.year - a.year);

    return list;
  }, [collection, genre, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, requestedPage), totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const intro = collectionIntro[collection] ?? collectionIntro.all;
  const currentLabel = collectionLabels[collection] ?? "全部影片";

  usePageMeta(
    `${intro.title} - 韩国三级电影`,
    `${intro.desc.slice(0, 60)}，支持按类型、年份筛选与热度排序，快速找到想看的韩国影视作品。`,
    `韩国三级电影,${currentLabel},韩国电影分类,影片筛选`,
  );

  const goPage = (page: number) => {
    const next = new URLSearchParams(searchParams);
    if (page <= 1) next.delete("page");
    else next.set("page", String(page));
    setSearchParams(next);
  };

  return (
    <SiteLayout>
      <div className="w-full border-b border-background-200/60 bg-background-100/40">
        <div className="mx-auto w-full max-w-[1360px] px-4 pb-8 pt-6 md:px-8 md:pb-10 md:pt-8">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影片分类", to: "/category" }, { label: currentLabel }]} />

          <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <span className="font-label text-[11px] uppercase tracking-[0.28em] text-accent-500">
                Category
              </span>
              <h1 className="mt-2 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-[34px]">
                {intro.title}
              </h1>
              <p className="mt-3 text-sm leading-7 text-foreground-500">{intro.desc}</p>
            </div>
            <div className="flex items-center gap-2 overflow-x-auto">
              {categoryTiles.map((tile) => (
                <Link
                  key={tile.key}
                  to={tile.to}
                  className={`shrink-0 whitespace-nowrap rounded-md border px-3 py-1.5 text-xs transition-colors ${
                    tile.key === collection
                      ? "border-primary-400 bg-primary-500 text-background-50"
                      : "border-background-200 bg-background-50 text-foreground-600 hover:border-primary-400"
                  }`}
                >
                  {tile.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1360px] px-4 py-8 md:px-8 md:py-10">
        <FilterBar
          genre={genre}
          year={year}
          sort={sort}
          total={filtered.length}
          onGenreChange={(value) => updateParam("genre", value, "全部")}
          onYearChange={(value) => updateParam("year", value, "all")}
          onSortChange={(value) => updateParam("sort", value, "latest")}
          onReset={resetFilters}
        />

        <div className="mt-7">
          {filtered.length === 0 ? (
            <EmptyState
              icon="ri-film-line"
              title="没有符合条件的影片"
              desc="当前筛选组合下暂时没有资料。可以试着放宽年份范围，或点击重置筛选回到全部影片。"
              resetLabel="重置筛选"
              onReset={resetFilters}
            />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 md:hidden">
                {filtered.slice(0, visible).map((movie, index) => (
                  <MovieCard key={movie.id} movie={movie} index={index} showDesc />
                ))}
              </div>

              <div className="hidden grid-cols-2 gap-4 md:grid md:grid-cols-3 lg:grid-cols-4">
                {paged.map((movie, index) => (
                  <MovieCard key={movie.id} movie={movie} index={index} showDesc />
                ))}
              </div>

              {visible < filtered.length ? (
                <div className="mt-6 flex justify-center md:hidden">
                  <button
                    type="button"
                    onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                    className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-400"
                  >
                    <i className="ri-add-line"></i>
                    加载更多（{filtered.length - visible}）
                  </button>
                </div>
              ) : null}

              {totalPages > 1 && (
                <div className="mt-9 hidden items-center justify-center gap-2 md:flex">
                  <button
                    type="button"
                    onClick={() => goPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border px-3 py-2 text-xs transition-colors ${
                      currentPage === 1
                        ? "cursor-not-allowed border-background-200 text-foreground-400"
                        : "border-background-300 text-foreground-600 hover:border-accent-500 hover:text-accent-400"
                    }`}
                  >
                    <i className="ri-arrow-left-s-line"></i>
                    上一页
                  </button>

                  {Array.from({ length: totalPages }).map((_, index) => {
                    const pageNumber = index + 1;
                    return (
                      <button
                        key={pageNumber}
                        type="button"
                        onClick={() => goPage(pageNumber)}
                        className={`h-9 w-9 cursor-pointer rounded-md text-xs transition-colors ${
                          pageNumber === currentPage
                            ? "bg-primary-500 font-semibold text-background-50"
                            : "border border-background-300 text-foreground-600 hover:border-accent-500 hover:text-accent-400"
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => goPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border px-3 py-2 text-xs transition-colors ${
                      currentPage === totalPages
                        ? "cursor-not-allowed border-background-200 text-foreground-400"
                        : "border-background-300 text-foreground-600 hover:border-accent-500 hover:text-accent-400"
                    }`}
                  >
                    下一页
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}

              <p className="mt-6 text-center text-[11px] text-foreground-500 md:mt-4">
                第 {currentPage} / {totalPages} 页 · 共 {filtered.length} 部影片，筛选与排序均为前端演示数据
              </p>
            </>
          )}
        </div>
      </div>
    </SiteLayout>
  );
}